import { useCallback, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { deleteQueryParams, updateQueryParam } from 'utils/queryParamsUtil';
import {
  FILTER_PARAM_EXTERNAL_ID,
  FILTER_PARAM_NAME,
  FILTER_PARAM_TYPE,
  FILTER_TYPE_SEPARATOR
} from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignRow } from 'views/OfferDesign/OfferDesign.types';
import { filterOffers, getArrayParam, getParam } from 'views/OfferDesign/OfferDesign.utils';

export const useOfferDesignFilters = (offers: OfferDesignRow[]) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const externalIdFilter = getParam(params, FILTER_PARAM_EXTERNAL_ID);
  const nameFilter = getParam(params, FILTER_PARAM_NAME);
  const typeFilter = getArrayParam(params, FILTER_PARAM_TYPE);

  const handleExternalIdChange = useCallback(
    (value: string) => updateQueryParam(FILTER_PARAM_EXTERNAL_ID, value, location, navigate),
    [location, navigate]
  );

  const handleNameChange = useCallback(
    (value: string) => updateQueryParam(FILTER_PARAM_NAME, value, location, navigate),
    [location, navigate]
  );

  const handleTypesSelectionChange = useCallback(
    (items: string[]) => {
      updateQueryParam(
        FILTER_PARAM_TYPE,
        items.join(FILTER_TYPE_SEPARATOR),
        location,
        navigate
      );
    },
    [location, navigate]
  );

  const handleClearFilters = useCallback(
    () => deleteQueryParams(
      [FILTER_PARAM_EXTERNAL_ID, FILTER_PARAM_NAME, FILTER_PARAM_TYPE],
      location,
      navigate
    ),
    [location, navigate]
  );

  const filteredOffers = useMemo(
    () => filterOffers(offers, {
      externalId: externalIdFilter,
      name: nameFilter,
      types: typeFilter
    }),
    [offers, externalIdFilter, nameFilter, typeFilter]
  );

  return {
    externalIdFilter,
    nameFilter,
    typeFilter,
    filteredOffers,
    handleExternalIdChange,
    handleNameChange,
    handleTypesSelectionChange,
    handleClearFilters
  };
};
